import find from 'latest_modules/lodash/find';
import assign from 'latest_modules/lodash/assign';
import isArray from 'latest_modules/lodash/isArray';
import isInteger from 'latest_modules/lodash/isInteger';

import ProductSku from '../schema/ProductSku';

import productsApi from '../../api/products';
import { PRODUCT_SKUS as productSkusTypes } from '../mutation-types';

// State
const state = () => ({
  selectedSkuId: null,
  list: []
});
// Getters
const getters = {
  /*
  Find a sku by sku id
  @param {String} id - SKU id
  @return {Object} ProductSku
  */
  findById(state) {
    return function (id) {
      if (id === null) {
        return null;
      }
      return find(state.list, { _id: id });
    };
  },

  /*
  Get SKU array of countries that allowed be sold
  @return {Array}
  */
  allowedCountrySkus(state) {
    const skus = [];
    for (let sku of state.list) {
      if (!sku.is_denied_country) {
        skus.push(sku);
      }
    }
    return skus;
  },

  /*
  Get a selected sku data (helper)
  @return {Object} ProductSku
  */
  selectedSku(state, getters) {
    if (!state.selectedSkuId) {
      return new ProductSku().toObject();
    }
    return getters.findById(state.selectedSkuId);
  }
};

// Actions
const actions = {
  /*
  Select a sku
  デフォルトで国制限されていない最初のSku、または全部制限されていたらリストの最初のSkuを選択する。
  @param {Object} payload
  @param {String} payload._id - SKU id
  */
  select({ commit, getters, rootState, state }, payload) {
    if (!(payload && payload._id)) {
      // ランディング時はOutOfStockでないSkuを選択する
      // ※全てOutOfStockなら最初のSkuを選択
      let sku = null;
      if (getters.allowedCountrySkus.length) {
        sku = getters.allowedCountrySkus.find(
          (sku) => sku.is_out_of_stock === false
        );
        if (!sku) {
          sku = getters.allowedCountrySkus[0];
        }
      } else if (state.list.length) {
        sku = state.list.find((sku) => sku.is_out_of_stock === false);
        if (!sku) {
          sku = state.list[0];
        }
      }

      if (sku) {
        let skuStatus;
        const sellingPrice = ProductSku.getSellingPrice(sku, {
          is_premium_member: rootState.user.is_premium_member,
          // ランディング時に選択されたSKUにPremium会員価格が設定されていれば
          // 強制的にプレミアム入会フラグ（will_join_premium）がtrueになるので、
          // userオブジェクトを渡さずにsku.has_premium_priceを見るようにしている
          will_join_premium: sku.has_premium_price
        });
        if (sku.is_out_of_stock) {
          skuStatus = 'OutOfStock';
        }
        if (sku.is_out_of_stock && sku.is_discontinued) {
          skuStatus = 'SoldOut';
        }

        // GA
        window.tom.helper.ga.detail(
          {
            id: sku._id,
            name: sku.titles.en_us,
            category:
              rootState.product.category && rootState.product.category.join('/'),
            price: sellingPrice,
            variant: sku.short_titles.en_us || sku.titles.en_us
          },
          { currencyCode: 'USD' }
        );

        if (skuStatus) {
          window.tom.helper.ga.push([
            'event',
            'EC',
            `landed${skuStatus}`,
            window.location.href,
            1,
            true
          ]);
        }
      }

      payload = { _id: sku && sku._id };
    }
    commit(productSkusTypes.SELECTED, payload);
    return Promise.resolve();
  },

  /*
  Set skus data (Call from product module only)
  @param {Array} payload.skus
  */
  setData({ commit, dispatch }, payload) {
    commit(productSkusTypes.DATA_RECEIVED, payload);
    return dispatch('select');
  },

  /*
  Add to or Remove from Wish List(Favorites)
  @param {Object} payload
  @param {String} payload.sku_id - SKU id
  @param {Boolean} payload.is_added - Current added flag
  */
  toggleWishList({ commit }, payload) {
    if (!(window.vueEventBus && window.vueEventBus.wishlistModal)) {
      return;
    }
    return new Promise(function (resolve) {
      window.vueEventBus.wishlistModal.$emit('addToWishlists', {
        productId: payload.product_id,
        skuId: payload.sku_id
      });

      window.vueEventBus.wishlistModal
        .$off('closeWishlistModal')
        .$on('closeWishlistModal', function (data) {
          commit(productSkusTypes.WISH_LIST_ITEM_CHANGED, {
            sku_id: payload.sku_id,
            status: data.isInWishlist
          });
          resolve();
        });
    });
  },

  /*
  Register to notify email when re-stocked product.
  @param {Object} payload
  @param {String} payload.product_id - Product id
  @param {String} payload.sku_id - SKU id
  @param {String} payload.email - Email (Only user who not logged in)
  @param {String} payload._csrf - csrf
  */
  registerRestockNotification({ commit }, payload) {
    return new Promise(function (resolve, reject) {
      productsApi.registerRestockNotification(
        {
          product_id: payload.product_id,
          sku_id: payload.sku_id,
          email: payload.email,
          _csrf: payload._csrf
        },
        function (err, res) {
          if (err) {
            return reject(err);
          }
          res = assign(res, {
            product_id: payload.product_id,
            sku_id: payload.sku_id,
            status: true
          });
          commit(productSkusTypes.RESTOCK_NOTIFICATION_REGISTERED, res);
          resolve();
        }
      );
    });
  },

  /*
  Unregister to notify email when re-stocked product.
  @param {Object} payload
  @param {String} payload.product_id - Product id
  @param {String} payload.sku_id - SKU id
  @param {String} payload.email - Email (Only user who not logged in)
  @param {String} payload._csrf - csrf
  */
  unregisterRestockNotification({ commit }, payload) {
    return new Promise(function (resolve, reject) {
      productsApi.unregisterRestockNotification(
        {
          product_id: payload.product_id,
          sku_id: payload.sku_id,
          email: payload.email,
          _csrf: payload._csrf
        },
        function (err, res) {
          if (err) {
            return reject(err);
          }
          res = assign(res, {
            product_id: payload.product_id,
            sku_id: payload.sku_id,
            status: false
          });
          commit(productSkusTypes.RESTOCK_NOTIFICATION_REGISTERED, res);
          resolve();
        }
      );
    });
  },

  /*
  Change stock count
  @param {Object} payload
  @param {Boolean} payload.sku_id - SKU id
  @param {Boolean} payload.count - Stock count
  */
  changeStockCount({ commit }, payload) {
    return new Promise(function (resolve, reject) {
      if (!isInteger(payload.count)) {
        return reject();
      }
      commit(productSkusTypes.CHANGE_STOCK_COUNT, {
        sku_id: payload.sku_id,
        count: payload.count
      });
      resolve();
    });
  }
};

// Mutations
const mutations = {
  [productSkusTypes.DATA_RECEIVED](state, payload) {
    if (isArray(payload)) {
      payload.map((sku) => state.list.push(new ProductSku(sku).toObject()));
    } else {
      state.list.push(new ProductSku(payload).toObject());
    }
  },

  [productSkusTypes.SELECTED](state, payload) {
    state.selectedSkuId = payload._id;
  },

  [productSkusTypes.WISH_LIST_ITEM_CHANGED](state, payload) {
    const sku = find(state.list, { _id: payload.sku_id });
    sku.is_added_to_wishlist = payload.status;
  },

  [productSkusTypes.RESTOCK_NOTIFICATION_REGISTERED](state, payload) {
    const sku = find(state.list, { _id: payload.sku_id });
    sku.is_notify_registered = payload.status;
  },

  [productSkusTypes.CHANGE_STOCK_COUNT](state, payload) {
    const sku = find(state.list, { _id: payload.sku_id });
    sku.stock_count = payload.count;
    // 在庫が1つだけのアイテムをカートに入れてから該当アイテムの詳細ページにランディングし、
    // カートモーダルからアイテムを削除した場合にはis_out_of_stockプロパティをfalseにする必要がある
    if (sku.stock_count > 0) {
      sku.is_out_of_stock = false;
    }
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
